import { public_env } from '@cfixe-monorepo/shared';

export type CapInstance = {
  solve: () => Promise<{ success: boolean; token: string }>;
  reset: () => void;
  /** L'élément `<cap-widget>` que Cap.js accroche sous `<html>` (absent sur une instance d'un autre type). */
  widget?: { isConnected: boolean };
};

// `solveQueue` : file FIFO interne des solve() (cf. getCaptchaToken). Optionnel → les refs existantes
// (`{ current }`, y compris les React refs) restent compatibles ; getCaptchaToken la renseigne.
export type CapRef = { current: CapInstance | null; solveQueue?: Promise<unknown> };

// Endpoint du widget captcha depuis une base API (ex. « https://back.../ », ou « '' » relatif proxifié
// côté frontend-web). Point UNIQUE qui connaît le chemin `public/captcha/`.
function captchaApiEndpoint(apiBase: string): string {
  const base = apiBase.endsWith('/') ? apiBase : `${apiBase}/`;
  return `${base}public/captcha/`;
}

// Cap.js accroche son `<cap-widget>` caché directement sous `<html>`. Quand React régénère l'arbre
// (décalage d'hydratation : extension navigateur type gestionnaire de mots de passe qui touche le DOM
// avant React), l'élément est DÉTACHÉ → `disconnectedCallback` vide son état et tout `solve()` jette
// « Cannot read properties of null (reading 'state') » : plus aucun token, connexion impossible avec
// CAPTCHA_REQUIRED (vu en recette le 31/10). Un widget détaché est jeté et recréé.
const isUsable = (cap: CapInstance | null): cap is CapInstance =>
  cap !== null && (cap.widget === undefined || cap.widget.isConnected);

async function getOrCreateCap(capRef: CapRef, apiBase: string): Promise<CapInstance> {
  if (isUsable(capRef.current)) return capRef.current;
  const { default: Cap } = await import('@cap.js/widget');
  capRef.current = new Cap({ apiEndpoint: captchaApiEndpoint(apiBase) });
  return capRef.current;
}

async function solveOnce(capRef: CapRef, apiBase: string): Promise<string | null> {
  try {
    const cap = await getOrCreateCap(capRef, apiBase);
    const result = await cap.solve();
    return result.success && result.token ? result.token : null;
  } catch {
    return null;
  }
}

// Cap.js ne supporte qu'UN seul solve() concurrent (le 2e renvoie undefined), et le backend consomme
// le token au 1er appel (single-use) → on ne peut pas partager une même promesse entre callers. On
// sérialise donc les solve() dans une file FIFO PAR capRef : un seul à la fois, chacun reçoit un token
// unique. Sans ça, plusieurs callbacks concurrents (ex. les 4 du tunnel VTC : 2 champs d'adresse +
// prix + checkout, sur le même capRef) se percutaient → « Captcha failed » intermittent.
//
// `apiBase` par défaut = public_env.API_ENDPOINT (cas des consommateurs shared-components) ; les apps
// qui résolvent l'API autrement (frontend-web proxifié, admin) passent leur propre base.
export async function getCaptchaToken(
  capRef: CapRef,
  apiBase: string = public_env.API_ENDPOINT,
): Promise<string | null> {
  const previous = capRef.solveQueue ?? Promise.resolve();
  const next = previous.then((): Promise<string | null> => solveOnce(capRef, apiBase));
  // On chaîne sur la version qui avale les rejets pour ne pas bloquer les callers suivants.
  capRef.solveQueue = next.catch((): null => null);
  return next;
}

export async function getRequiredCaptchaToken(
  capRef: CapRef,
  apiBase: string = public_env.API_ENDPOINT,
): Promise<string> {
  const token = await getCaptchaToken(capRef, apiBase);
  if (!token) throw new Error('Captcha failed');
  return token;
}

/** Précharge le module Cap.js + WASM pour accélérer la 1re résolution (au 1er 403). */
export function warmupCaptcha(capRef: CapRef, apiBase: string = public_env.API_ENDPOINT): void {
  getOrCreateCap(capRef, apiBase).catch((): undefined => undefined);
}
